import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import styles from "./EditCompanyPageStyles.module.css";

const EditCompanyPage = () => {
  // 1. Params & Navigation
  const { companycode } = useParams();
  const navigate = useNavigate();

  // 2. Form State
  const [companyName, setCompanyName] = useState("");
  const [adminFirstName, setAdminFirstName] = useState("");
  const [adminLastName, setAdminLastName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const adminType = "2";

  // 3. UI / Validation
  const [formErrors, setFormErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  // 4. Fetch Existing Company Details
  const fetchCompanyDetails = async () => {
    try {
      const adminEmailLS = localStorage.getItem("adminemail") || "";
      const response = await axios.post(
        "https://casaahaanahotels.com/sb_showcompanyusers",
        { adminemail: adminEmailLS }
      );
      const users = response.data?.user_data || [];
      const company = users.find((c) => c.admincode === companycode);
      if (company) {
        setCompanyName(company.companyname || "");
        setAdminFirstName(company.admin_first_name || "");
        setAdminLastName(company.admin_last_name || "");
        // initialize phone (raw) then formatter will run if user edits
        setContactPhone(company.contactphone || company.contactPhone || "");
        setAdminEmail(company.adminemail || "");
      } else {
        setErrorMessage("Company not found.");
      }
    } catch (err) {
      console.error(err);
      setErrorMessage("Failed to fetch company details.");
    }
  };

  useEffect(() => {
    fetchCompanyDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companycode]);

  // 5. Validation
  const isValidEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validateForm = () => {
    const errors = {};
    if (adminEmail && !isValidEmail(adminEmail)) {
      errors.adminEmail = "Invalid email format";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // 6. Clear Form
  const clearForm = () => {
    setCompanyName("");
    setAdminFirstName("");
    setAdminLastName("");
    setContactPhone("");
    setAdminEmail("");
    setFormErrors({});
    setErrorMessage("");
    setSuccessMessage("");
  };

  // 7. US-style Phone Formatter
  const handlePhoneChange = (e) => {
    let digits = e.target.value.replace(/\D/g, "").substring(0, 10);
    let formatted = digits;
    if (digits.length > 6) {
      formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else if (digits.length > 3) {
      formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
    }
    setContactPhone(formatted);
  };

  // 8. Save -> Call edit API
  const handleSave = async () => {
    if (!validateForm()) return;

    const payload = {
      admincode: companycode,
      companyName,
      admin_first_name: adminFirstName,
      admin_last_name: adminLastName,
      contactPhone,
      admin_type: adminType,
      company_department: "",
      admin_password: "",
      adminemails: adminEmail,
      adminpageheader: "",
      adminpagecontent: "",
      adminemailadminpageoffers: "",
      adminpagecta: "",
      assessmentRecipient: "",
    };

    try {
      const res = await axios.post(
        "https://casaahaanahotels.com/sb_editcompanyuser",
        payload,
        { headers: { "Content-Type": "application/json" } }
      );
      const msg = res.data?.Message || "";
      if (msg.toLowerCase().includes("success")) {
        setSuccessMessage("Company updated successfully!");
        setIsModalOpen(true);
      } else {
        setErrorMessage(msg || "Failed to update company.");
      }
    } catch (err) {
      console.error(err);
      setErrorMessage("An error occurred while saving the form.");
    }
  };

  // 9. Early error render
  if (errorMessage && !isModalOpen) {
    return <div className={styles.errorMessage}>{errorMessage}</div>;
  }

  // 10. Main Render
  return (
    <div className={styles.formContainer}>
      <h2>Edit Company</h2>
      <h3>{companyName}</h3>

      {errorMessage && !isModalOpen && (
        <div className={styles.errorMessage}>{errorMessage}</div>
      )}

      <form className={styles.assignForm}>
        {/* Company Name */}
        <div className={styles.formRow}>
          <input
            type="text"
            placeholder="Company Name"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            className={
              formErrors.companyName
                ? styles.errorInput
                : styles.selectField
            }
          />
        </div>

        {/* First Name */}
        <div className={styles.formRow}>
          <input
            type="text"
            placeholder="First Name"
            value={adminFirstName}
            onChange={(e) => setAdminFirstName(e.target.value)}
            className={
              formErrors.adminFirstName
                ? styles.errorInput
                : styles.selectField
            }
          />
        </div>

        {/* Last Name */}
        <div className={styles.formRow}>
          <input
            type="text"
            placeholder="Last Name"
            value={adminLastName}
            onChange={(e) => setAdminLastName(e.target.value)}
            className={
              formErrors.adminLastName
                ? styles.errorInput
                : styles.selectField
            }
          />
        </div>

        {/* Contact Phone (formatted) */}
        <div className={styles.formRow}>
          <input
            type="tel"
            placeholder="Contact Phone (123-456-7890)"
            value={contactPhone}
            onChange={handlePhoneChange}
            pattern="\d{3}-\d{3}-\d{4}"
            className={
              formErrors.contactPhone
                ? styles.errorInput
                : styles.selectField
            }
          />
        </div>

        {/* Email */}
        <div className={styles.formRow}>
          <input
            type="email"
            placeholder="Email"
            value={adminEmail}
            onChange={(e) => setAdminEmail(e.target.value)}
            className={
              formErrors.adminEmail
                ? styles.errorInput
                : styles.selectField
            }
          />
        </div>

        {/* Save / Clear Buttons */}
        <div className={styles.buttonContainer}>
          <button
            type="button"
            onClick={handleSave}
            className={styles.submitButton}
          >
            Save
          </button>
          <button
            type="button"
            onClick={clearForm}
            className={styles.submitButton}
            style={{ backgroundColor: "red" }}
          >
            Clear
          </button>
        </div>
      </form>

      {/* Success Modal */}
      {isModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h2>Success!</h2>
            <p>{successMessage}</p>
            <button
              onClick={() => {
                setIsModalOpen(false);
                navigate("/company/view-company");
              }}
              className={styles.okButton}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditCompanyPage;
